import cls from 'classnames'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FormTitle } from '@/pages/ProjectCreatePage/components/StepOne/StepOne'
import MiddleText from '@/components/MiddleText/MiddleText'
import VideoAnalysisChart from '../../VideoAnalysisChart/VideoAnalysisChart'
import styles from '../../VideoAnalysis.module.less'
import {
  PROFESSOR_RATE_BTN_ID_PREFIX,
  RateBtn,
} from '@/pages/ProfessorProjectDetailPage/components/ProfessorRatePanel/ProfessorRatePanel'

type Props = {
  chartRerender: boolean
  mode?: 'professor' | 'default'
  project: ProjectModels.ProjectDetail
  viewMode: 'realtime' | 'panoramic'
  onViewModeChange: (viewMode: 'realtime' | 'panoramic') => void
  onAxisClick: (second: number) => void
  onRateClick: () => void
}

const PORTRAIT_CURVE_WIDTH = 450
const PORTRAIT_SPLASH_VIDEO_WIDTH = 300
const PORTRAIT_SPLASH_CHART_WIDTH = PORTRAIT_SPLASH_VIDEO_WIDTH + 28 + 15
const PORTRAIT_SPLASH_CURVE_WIDTH = 654
const PORTRAIT_REGULAR_CURVE_WIDTH = 798

function normalizeSplashProject(project: ProjectModels.ProjectDetail) {
  const durationValue = Number(project.adDuration)
  const safeDurationSecondCount =
    Number.isFinite(durationValue) && durationValue > 0 ? Math.ceil(durationValue) : 0
  const maxMetricSecond = project.detailMetrics.reduce((max, item) => {
    return Number.isFinite(item.secondCount) ? Math.max(max, item.secondCount) : max
  }, 0)
  const maxFrameSecond = project.frameInfos.reduce((max, item) => {
    const second = Number(item.secondCount)
    return Number.isFinite(second) ? Math.max(max, second) : max
  }, 0)
  const targetSecondCount = Math.min(
    10,
    Math.max(5, project.detailMetrics.length, project.frameInfos.length, maxMetricSecond, maxFrameSecond, safeDurationSecondCount)
  )
  const metricsBySecond = new Map(project.detailMetrics.map((item) => [item.secondCount, item]))
  const frameInfosBySecond = new Map(
    project.frameInfos.map((item) => [Number(item.secondCount), item])
  )
  const fallbackMetric = project.detailMetrics[project.detailMetrics.length - 1]
  const fallbackFrameInfo = project.frameInfos[project.frameInfos.length - 1]

  if (!fallbackMetric || !fallbackFrameInfo || targetSecondCount <= project.detailMetrics.length) {
    return project
  }

  const detailMetrics = Array.from({ length: targetSecondCount }, (_, index) => {
    const second = index + 1
    const metric = metricsBySecond.get(second)
    if (metric) {
      return metric
    }
    const prevMetric = metricsBySecond.get(second - 1) || fallbackMetric
    return {
      ...prevMetric,
      secondCount: second,
    }
  })

  const frameInfos = Array.from({ length: targetSecondCount }, (_, index) => {
    const second = index + 1
    const frameInfo = frameInfosBySecond.get(second)
    if (frameInfo) {
      return frameInfo
    }
    const prevFrameInfo = frameInfosBySecond.get(second - 1) || fallbackFrameInfo
    return {
      ...prevFrameInfo,
      secondCount: String(second),
    }
  })

  return {
    ...project,
    detailMetrics,
    frameInfos,
  }
}

function SubjectiveResponseCurvePortrait(props: Props) {
  const { t } = useTranslation()
  const adDuration = Number(props.project.adDuration || 0)
  const metricSeconds = props.project.detailMetrics.length
  const frameSeconds = props.project.frameInfos.length
  const inferredSecondCount = Math.max(
    metricSeconds,
    frameSeconds,
    Number.isFinite(adDuration) && adDuration > 0 ? Math.ceil(adDuration) : 0
  )
  const isSplashCreative =
    props.project.durationCategory === 'five_to_ten' ||
    (props.project.durationCategory !== 'ten_plus' &&
      inferredSecondCount > 0 &&
      inferredSecondCount <= 10)
  const isRegularCreative = !isSplashCreative
  const containerWidth = isSplashCreative
    ? PORTRAIT_SPLASH_CURVE_WIDTH
    : isRegularCreative
      ? PORTRAIT_REGULAR_CURVE_WIDTH
      : PORTRAIT_CURVE_WIDTH
  const chartProject = useMemo(() => {
    if (!isSplashCreative) {
      return props.project
    }
    return normalizeSplashProject(props.project)
  }, [isSplashCreative, props.project])
  return (
    <>
      <FormTitle
        text={
          <>
            <span>
              <MiddleText text={t('dashboard.projectDetailPage.videoView.subjectiveResponseCurve')} />
            </span>
            <span className={styles.switchBtn}>
              <span className={cls(styles.viewBtn, props.viewMode === 'realtime' && styles.active)} onClick={() => {
                props.onViewModeChange('realtime')
              }}>
                <MiddleText text={t('dashboard.projectDetailPage.videoView.realtimeData')} />
              </span>
              <span className={cls(styles.viewBtn, props.viewMode === 'panoramic' && styles.active)} onClick={() => {
                props.onViewModeChange('panoramic')
              }}>
                <MiddleText text={t('dashboard.projectDetailPage.videoView.panoramicData')} />
              </span>
            </span>
            {props.mode === 'professor' && (
              <span id={`${PROFESSOR_RATE_BTN_ID_PREFIX}-subjectiveResponseCurve`}>
                <RateBtn
                  rateKey={'subjective_response_curve'}
                  style={{ marginLeft: 8 }}
                  onClick={props.onRateClick}
                />
              </span>
            )}
          </>
        }
        style={{ marginTop: 32 }}
      />

      <div className={styles.videoAnalysis} style={{ maxWidth: containerWidth, minWidth: containerWidth }}>
        {props.chartRerender && (
          <div className={styles.videoAnalysisChartWrapper} style={{ width: containerWidth }}>
            <VideoAnalysisChart
              orientation='portrait'
              viewMode={props.viewMode}
              mode={props.mode}
              project={chartProject}
              onAxisClick={props.onAxisClick}
              layout={isSplashCreative ? 'portrait-splash' : isRegularCreative ? 'portrait-regular' : 'default'}
              chartWidth={isSplashCreative ? PORTRAIT_SPLASH_CHART_WIDTH : isRegularCreative ? PORTRAIT_REGULAR_CURVE_WIDTH : PORTRAIT_CURVE_WIDTH}
            />
          </div>
        )}
        <div className={styles.legendBox} style={{ width: containerWidth }}></div>
      </div>
    </>
  )
}

export default SubjectiveResponseCurvePortrait
